import React from 'react';

/**
 * MetricDetailPane Component
 * 
 * Displays detailed table view for a selected metric.
 * Handles both job-based and submission-based metrics.
 * 
 * @param {Object} metric - Selected metric data { label, count, color, data }
 * @param {string} metricKey - Key identifying the metric type
 * @param {Array} jobs - All jobs data (for lookups)
 * @param {Array} candidates - All candidates data (for lookups)
 * @param {Array} submissions - Filtered submissions for counting
 * @param {Function} getColorClasses - Function to get color classes for gradient
 */
const MetricDetailPane = ({ 
  metric, 
  metricKey, 
  jobs, 
  candidates, 
  submissions,
  getColorClasses 
}) => {
  if (!metric || !metric.data) return null;

  const isJobMetric = ['jobsSubmittedTo', 'currentOpenJobs', 'jobsFilled'].includes(metricKey);
  
  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${getColorClasses(metric.color).gradient} px-6 py-4 border-b border-gray-200`}>
        <h4 className="text-lg font-semibold text-gray-900">
          {metric.label} - Details
        </h4>
        <p className="text-sm text-gray-600">
          {metric.count} item{metric.count !== 1 ? 's' : ''} in selected timeframe
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        {isJobMetric ? (
          <JobsTable 
            jobs={metric.data} 
            metricKey={metricKey}
            submissions={submissions}
          />
        ) : (
          <SubmissionsTable 
            submissions={metric.data}
            jobs={jobs}
            candidates={candidates}
          />
        )}
      </div>
    </div>
  );
};

/**
 * JobsTable - Displays jobs in a table format
 */
const JobsTable = ({ jobs, metricKey, submissions }) => {
  return (
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
          {metricKey === 'jobsSubmittedTo' && (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
          )}
          {metricKey === 'jobsFilled' && (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Placed</th>
          )}
          {metricKey === 'currentOpenJobs' && (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
          )}
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {jobs.map((job, index) => {
          const jobSubmissions = submissions?.filter(sub => sub.job_id === job.id) || [];
          const placedCount = jobSubmissions.filter(sub => sub.status === 'Onboarded').length;
          
          return (
            <tr key={job.id || index} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{job.title || 'Untitled'}</div>
                <div className="text-xs text-gray-500">{job.external_id || ''}</div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">
                {job.client_company || 'N/A'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">
                {job.location || job.city || 'N/A'}
              </td>
              {metricKey === 'jobsSubmittedTo' && (
                <td className="px-4 py-3 text-sm text-gray-700">
                  {jobSubmissions.length}
                </td>
              )}
              {metricKey === 'jobsFilled' && (
                <td className="px-4 py-3 text-sm text-gray-700">
                  {placedCount}
                </td>
              )}
              {metricKey === 'currentOpenJobs' && (
                <td className="px-4 py-3 text-sm text-gray-700">
                  {job.date_added ? new Date(job.date_added).toLocaleDateString() : 'N/A'}
                </td>
              )}
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  metricKey === 'currentOpenJobs' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {job.status || 'N/A'}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/**
 * SubmissionsTable - Displays submissions in a table format
 */
const SubmissionsTable = ({ submissions, jobs, candidates }) => {
  return (
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {submissions.map((submission, index) => {
          const job = jobs?.find(j => j.id === submission.job_id);
          const candidate = candidates?.find(c => c.id === submission.candidate_id);
          
          return (
            <tr key={submission.id || index} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">
                  {candidate ? `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() : 'Unknown'}
                </div>
                <div className="text-xs text-gray-500">{candidate?.email || ''}</div>
              </td>
              <td className="px-4 py-3">
                <div className="text-sm text-gray-700">{job?.title || 'N/A'}</div>
                <div className="text-xs text-gray-500">{job?.client_company || ''}</div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">
                {submission.date_added ? new Date(submission.date_added).toLocaleDateString() : 'N/A'}
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                  {submission.status || 'N/A'}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MetricDetailPane;
